import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

// --- MUI Imports ---
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';

// --- React Components ---

const LabelPreview = ({ data }) => {
  const { brand, productName, size, color, wasPrice, nowPrice, barcode } = data;

  let sizeColorString = '';
  if (size && color) {
    sizeColorString = `Size: ${size} Colour: ${color}`;
  } else if (size) {
    sizeColorString = `Size: ${size}`;
  } else if (color) {
    sizeColorString = `Colour: ${color}`;
  }

  const lineClampStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  };

  return (
    <Box
      sx={{
        width: '384px',
        height: '288px',
        fontFamily: "'Times New Roman', Times, serif",
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.400',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'center',
        p: 2,
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="h5" component="h2" fontWeight="bold" mb={0.5}>
        {brand}
      </Typography>

      <Typography
        variant="h6"
        component="h3"
        fontWeight="bold"
        mb={0.5}
        sx={{ ...lineClampStyle, height: '3rem' }}
      >
        {productName}
      </Typography>

      {sizeColorString && (
        <Typography variant="body2" fontStyle="italic" mb={0.5}>
          {sizeColorString}
        </Typography>
      )}

      <Box mb={0.5}>
        <Typography
          variant="h6"
          component="span"
          fontWeight="bold"
          sx={{
            textDecoration: 'line-through',
            position: 'relative',
            mr: 2,
          }}
        >
          Was:{wasPrice}
        </Typography>
        <Typography variant="h5" component="span" fontWeight="bold">
          Now: {nowPrice}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Box
          sx={{
            height: 48,
            width: 192,
            bgcolor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.600',
            fontSize: '0.75rem',
            fontFamily: 'sans-serif',
          }}
        >
          BARCODE PLACEHOLDER
        </Box>
        <Typography
          variant="body2"
          sx={{
            letterSpacing: '0.1em',
            fontFamily: 'sans-serif',
            mt: 0.5,
          }}
        >
          {barcode.replace(/^L/, '')}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Typography variant="h5" component="h4" fontWeight="bold">
        CLEARANCE
      </Typography>
    </Box>
  );
};

const generateZPLString = (data) => {
  const { brand, productName, size, color, barcode } = data;
  const wasPriceZPL = data.wasPrice.startsWith('£')
    ? `Was:${data.wasPrice}`
    : 'Was: N/A';
  const nowPriceZPL = data.nowPrice.startsWith('£')
    ? `Now:${data.nowPrice}`
    : 'Now: N/A';

  let sizeColorString = '';
  if (size && color) {
    sizeColorString = `Size: ${size} Colour: ${color}`;
  } else if (size) {
    sizeColorString = `Size: ${size}`;
  } else if (color) {
    sizeColorString = `Colour: ${color}`;
  }

  const labelHeight = 305;
  const labelWidth = 406;
  const wasPriceFontHeight = 25;
  const nowPriceFontHeight = 30;
  const priceY = 135;
  const wasPriceX = 20;
  const wasPriceWidth = wasPriceZPL.length * (wasPriceFontHeight / 2);

  let pnLine1 = productName;
  let pnLine2 = '';
  if (productName.length > 20) {
    let breakPoint = productName.lastIndexOf(' ', 20);
    if (breakPoint === -1) breakPoint = 20;
    pnLine1 = productName.substring(0, breakPoint);
    pnLine2 = productName.substring(breakPoint + 1);
  }

  const barcodeText = barcode.replace(/^L/, '');

  return `
^XA
^PW${labelWidth}
^LL${labelHeight}
^CI28
^CF0,30
^FO20,15^GB${labelWidth - 40},0,1^FS
^FO0,20^A0N,30,30^FB${labelWidth},1,0,C,0^FD${brand}^FS
^CF0,25
^FO0,55^A0N,25,25^FB${labelWidth},2,0,C,0^FD${pnLine1}^FS
^FO0,80^A0N,25,25^FB${labelWidth},1,0,C,0^FD${pnLine2}^FS
^CF0,20
^FO0,110^A0N,20,20^FB${labelWidth},1,0,C,0^FD${sizeColorString}^FS
^CF0,${wasPriceFontHeight}
^FO${wasPriceX},${priceY}^A0N,${wasPriceFontHeight},${wasPriceFontHeight}^FD${wasPriceZPL}^FS
${
  data.wasPrice.startsWith('£')
    ? `^FO${wasPriceX + 10},${
        priceY + wasPriceFontHeight / 2 - 1
      }^GB${wasPriceWidth},3,3^FS`
    : ''
}
^CF0,${nowPriceFontHeight}
^FO230,${priceY}^A0N,${nowPriceFontHeight},${nowPriceFontHeight}^FD${nowPriceZPL}^FS
^FO60,170^BCN,60,N,N,N,N^FD${barcode}^FS
^FO0,235^A0N,20,20^FB${labelWidth},1,0,C,0^FD${barcodeText}^FS
^CF0,40
^FO0,${labelHeight - 45}^A0N,40,40^FB${labelWidth},1,0,C,0^FDCLEARANCE^FS
^FO20,${labelHeight - 10}^GB${labelWidth - 40},0,1^FS
^XZ
`;
};

const useJsBarcode = (ref, text) => {
  useEffect(() => {
    if (ref.current && window.JsBarcode) {
      try {
        window.JsBarcode(ref.current, text, {
          format: 'CODE128',
          displayValue: true,
          text: text.replace(/^L/, ''),
          fontSize: 14,
          margin: 0,
          height: 50,
          width: 2,
        });
      } catch (e) {
        console.error("JsBarcode error:", e);
      }
    }
  }, [ref, text]);
};

const LabelPreviewPDF = ({ data }) => {
  const { brand, productName, size, color, wasPrice, nowPrice, barcode } = data;
  const barcodeRef = useRef(null);
  useJsBarcode(barcodeRef, barcode);

  let sizeColorString = '';
  if (size && color) {
    sizeColorString = `Size: ${size} Colour: ${color}`;
  } else if (size) {
    sizeColorString = `Size: ${size}`;
  } else if (color) {
    sizeColorString = `Colour: ${color}`;
  }

  const lineClampStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  };

  return (
    <Box
      sx={{
        width: '406px',
        height: '305px',
        fontFamily: "'Times New Roman', Times, serif",
        bgcolor: '#ffffff',
        border: 'none',
        color: '#000000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'center',
        padding: '12px',
        boxSizing: 'border-box',
      }}
    >
      <Typography 
        sx={{ 
          fontSize: '20px', 
          fontWeight: 'bold', 
          marginBottom: '4px',
          fontFamily: "'Times New Roman', Times, serif"
        }}
      >
        {brand}
      </Typography>

      <Typography
        sx={{ 
          ...lineClampStyle, 
          fontSize: '16px', 
          fontWeight: 'bold', 
          marginBottom: '4px',
          height: '38px',
          fontFamily: "'Times New Roman', Times, serif"
        }}
      >
        {productName}
      </Typography>

      {sizeColorString && (
        <Typography 
          sx={{ 
            fontSize: '13px', 
            fontStyle: 'italic', 
            marginBottom: '4px',
            fontFamily: "'Times New Roman', Times, serif"
          }}
        >
          {sizeColorString}
        </Typography>
      )}

      <Box sx={{ marginBottom: '4px' }}>
        <Typography
          component="span"
          sx={{ 
            fontSize: '16px', 
            fontWeight: 'bold',
            textDecoration: 'line-through', 
            marginRight: '12px',
            fontFamily: "'Times New Roman', Times, serif"
          }}
        >
          Was:{wasPrice}
        </Typography>
        <Typography 
          component="span" 
          sx={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            fontFamily: "'Times New Roman', Times, serif"
          }}
        >
          Now: {nowPrice}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <canvas ref={barcodeRef} />
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Typography 
        sx={{ 
          fontSize: '26px', 
          fontWeight: 'bold',
          fontFamily: "'Times New Roman', Times, serif"
        }}
      >
        CLEARANCE
      </Typography>
    </Box>
  );
};

export default function ClearanceLabels() {
  const [lotNumbersInput, setLotNumbersInput] = useState('');
  const [generatedLabels, setGeneratedLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isDownloadingZPL, setIsDownloadingZPL] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [libsLoaded, setLibsLoaded] = useState(false);
  const printContainerRef = useRef(null);

  // Printer states
  const [showPrinterDialog, setShowPrinterDialog] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    const scripts = [
      {
        name: 'jsPDF',
        src: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
        isLoaded: () => !!window.jspdf,
      },
      {
        name: 'html2canvas',
        src: 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
        isLoaded: () => !!window.html2canvas,
      },
      {
        name: 'JsBarcode',
        src: 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js',
        isLoaded: () => !!window.JsBarcode,
      },
    ];

    let scriptsToLoad = scripts.filter((s) => !s.isLoaded());
    if (scriptsToLoad.length === 0) {
      if (!libsLoaded) setLibsLoaded(true);
      return;
    }

    let scriptsLoadedCount = 0;

    const loadScript = (script) => {
      const s = document.createElement('script');
      s.src = script.src;
      s.async = true;
      s.onload = () => {
        scriptsLoadedCount++;
        if (scriptsLoadedCount === scriptsToLoad.length) {
          console.log('All PDF/Barcode libraries loaded successfully.');
          setLibsLoaded(true);
        }
      };
      s.onerror = () => {
        console.error(`Failed to load script: ${script.name}`);
        setErrors((prev) => [
          ...prev,
          { id: 'global', error: `Failed to load library: ${script.name}` },
        ]);
      };
      document.body.appendChild(s);
    };

    scriptsToLoad.forEach(loadScript);
  }, [libsLoaded]);

  // Fetch available printers from backend
  const fetchAvailablePrinters = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axiosInstance.get('/api/printers', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAvailablePrinters(response.data.printers || []);
      
      // Auto-select if only one printer
      if (response.data.printers && response.data.printers.length === 1) {
        setSelectedPrinter(response.data.printers[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch printers:', error);
      setErrors((prev) => [
        ...prev.filter((e) => e.id !== 'global'),
        { id: 'global', error: 'Failed to fetch available printers.' },
      ]);
    }
  };

  const handleGeneratePreview = async () => {
    setGeneratedLabels([]);
    setErrors([]);
    setIsLoading(true);

    const lotIds = lotNumbersInput
      .split('\n')
      .map((s) => s.trim().replace(/^L/i, ''))
      .filter(Boolean);

    if (lotIds.length === 0) {
      setErrors([{ id: 'global', error: 'Please enter at least one lot number.' }]);
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axiosInstance.post(
        '/api/clearance-labels',
        { lotNumbers: lotIds },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = response.data;
      setGeneratedLabels(data.labels || []);
      setErrors(data.errors || []);
    } catch (error) {
      console.error('Failed to fetch label data:', error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'A network error occurred.';
      setErrors([{ id: 'global', error: errorMsg }]);
    }

    setIsLoading(false);
  };

  const handleDownloadZPL = () => {
    if (generatedLabels.length === 0) {
      setErrors((prev) => [
        ...prev.filter((e) => e.id !== 'global'),
        { id: 'global', error: 'No labels to download.' },
      ]);
      return;
    }
    setIsDownloadingZPL(true);
    try {
      const zplString = generatedLabels
        .map((labelData) => generateZPLString(labelData))
        .join('\n');
      const blob = new Blob([zplString], {
        type: 'text/plain;charset=utf-8',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = 'clearance-labels.zpl';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate ZPL file:', error);
      setErrors((prev) => [
        ...prev,
        { id: 'global', error: 'Failed to generate .zpl file.' },
      ]);
    }
    setIsDownloadingZPL(false);
  };

/**
 * Draws a single label on the PDF document, using a SINGLE dynamic 'y'
 * coordinate from top-to-bottom to prevent ALL overlapping.
 *
 * v11 Changes:
 * - "Auto-Fit" Title Logic: Instead of truncating, it progressively
 * reduces the font size (from 11 down to 7) until the title fits
 * into 2 lines.
 */
const drawLabelOnPdf = (doc, data) => {
  const { brand, productName, size, color, wasPrice, nowPrice, barcode } = data;

  // --- Label Setup ---
  const labelWidth = 2.0;
  const labelHeight = 1.5;
  const center = labelWidth / 2;
  const margin = 0.1;

  // --- Border ---
  // doc.setLineWidth(0.01);
  // doc.roundedRect(margin / 2, margin / 2, labelWidth - margin, labelHeight - margin, 0.05, 0.05, 'S');

  // This is the single, flowing y-coordinate.
  let y = 0.25;

  // --- Brand ---
  doc.setFont('times', 'bold');
  doc.setFontSize(14);
  doc.text(brand || 'Brand', center, y, { align: 'center' });
  y += 0.18; // Gap after Brand

  // --- Product Title (Auto-Shrink to fit 2 lines) ---
  doc.setFont('times', 'bold');
  
  let fontSize = 11; // Start with standard size
  doc.setFontSize(fontSize);
  let titleLines = doc.splitTextToSize(productName || 'Product Title Goes Here', 1.7);

  // Loop: If text takes more than 2 lines, reduce font size until it fits
  // We stop if it fits OR if font gets too small (e.g., 6pt)
  while (titleLines.length > 2 && fontSize > 6) {
    fontSize -= 0.5; // Reduce font size by 0.5
    doc.setFontSize(fontSize); // Apply new size
    titleLines = doc.splitTextToSize(productName || 'Product Title Goes Here', 1.7); // Re-calculate lines
  }

  doc.text(titleLines, center, y, { align: 'center' });
  
  // Calculate vertical space based on the specific font size used
  // (fontSize / 72) converts points to inches approximately
  const lineHeight = fontSize * 0.015; 
  y += (titleLines.length * lineHeight) + 0.08;

  // --- Size/Colour (Conditional) ---
  if (size || color) {
    doc.setFont('times', 'bolditalic');
    doc.setFontSize(9);
    let sizeColorLine = '';
    if (size && color) sizeColorLine = `Size: ${size}   Colour: ${color}`;
    else if (size) sizeColorLine = `Size: ${size}`;
    else if (color) sizeColorLine = `Colour: ${color}`;

    doc.text(sizeColorLine, center, y, { align: 'center' });
    y += 0.14; // Gap after Size/Colour
  } else {
    y += 0.05; // Smaller gap if no Size/Colour
  }

  // --- Prices (Uses current 'y') ---
  const priceY = y;
  const leftX = 0.10;
  const rightX = labelWidth - 0.10; // 1.90

  // Was price (left-aligned)
  doc.setFont('times', 'bolditalic');
  doc.setFontSize(11);
  const wasText = `Was: ${wasPrice ? `${wasPrice}` : '£199.99'}`;
  doc.text(wasText, leftX, priceY);

  // Strikethrough
  const wasTextWidth = doc.getTextWidth(wasText);
  doc.setLineWidth(0.01);
  doc.line(leftX, priceY - 0.03, leftX + wasTextWidth, priceY - 0.03);

  // Now price (right-aligned)
  doc.setFont('times', 'bold');
  doc.setFontSize(11);
  const nowText = `Now: ${nowPrice ? `${nowPrice}` : '£149.99'}`;
  doc.text(nowText, rightX, priceY, { align: 'right' });

  // Gap after prices
  y += 0.13;

  // --- Barcode (Uses current 'y') ---
  const barcodeY = y;
  const barcodeHeight = 0.20;
  const barcodeWidth = 1.4;
  const barcodeX = (labelWidth - barcodeWidth) / 2;
  const barcodeCanvas = document.createElement('canvas');

  try {
    // High-Res Barcode Generation
    window.JsBarcode(barcodeCanvas, barcode || '123456789012', {
      format: 'CODE128',
      displayValue: false,
      width: 3,
      height: 60, // 0.20 inches * 300 DPI
      margin: 0,
    });
    const imgData = barcodeCanvas.toDataURL('image/png');
    doc.addImage(imgData, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
  } catch (err) {
    console.error('Barcode generation failed', err);
    doc.text('Barcode error', center, barcodeY + 0.1, { align: 'center' });
  }

  // Gap after barcode image
  y += barcodeHeight + 0.09;

  // --- Barcode Text (Uses current 'y') ---
  doc.setFont('times', 'normal');
  doc.setFontSize(8);
  doc.text(barcode || '123456789012', center, y, { align: 'center' });

  // Gap before CLEARANCE
  y += 0.13;

  // --- CLEARANCE (Uses current 'y') ---
  doc.setFont('times', 'bold');
  doc.setFontSize(10);
  doc.text('CLEARANCE', center, y, { align: 'center' });

  if (y > labelHeight - 0.05) {
     console.warn("Label content is too tall and may be clipped.");
  }
};

const handleDownloadPDF = async () => {
  if (generatedLabels.length === 0 || !printContainerRef.current) {
    setErrors((prev) => [
      ...prev.filter((e) => e.id !== 'global'),
      { id: 'global', error: 'No labels to download.' },
    ]);
    return;
  }

  if (!libsLoaded) {
    setErrors((prev) => [
      ...prev,
      { id: 'global', error: 'PDF libraries not loaded yet.' },
    ]);
    return;
  }

  setIsDownloadingPDF(true);
  await new Promise((resolve) => setTimeout(resolve, 500));

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'in',
    format: [2, 1.5],
  });

  generatedLabels.forEach((label, i) => {
    if (i > 0) doc.addPage([2, 1.5], 'l');
    drawLabelOnPdf(doc, label);
  });

  doc.save('clearance-labels.pdf');
  setIsDownloadingPDF(false);
};


  // Show printer selection dialog
  const handleOpenPrintDialog = async () => {
    await fetchAvailablePrinters();
    setShowPrinterDialog(true);
  };

  // Send ZPL to printer via backend
  const handlePrintToZebra = async () => {
    if (!selectedPrinter || generatedLabels.length === 0) {
      return;
    }

    setIsPrinting(true);
    setShowPrinterDialog(false);

    try {
      // Generate ZPL for all labels
      const zplString = generatedLabels
        .map((labelData) => generateZPLString(labelData))
        .join('\n');

      const token = localStorage.getItem('authToken');
      const response = await axiosInstance.post(
        '/api/print-labels',
        {
          printerName: selectedPrinter,
          zplData: zplString,
          labelCount: generatedLabels.length,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Clear errors and show success
        setErrors((prev) => prev.filter((e) => e.id !== 'global'));
        alert(`Successfully sent ${generatedLabels.length} label(s) to printer: ${selectedPrinter}`);
      } else {
        throw new Error(response.data.message || 'Print failed');
      }
    } catch (error) {
      console.error('Failed to print labels:', error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'Failed to send labels to printer.';
      setErrors((prev) => [
        ...prev.filter((e) => e.id !== 'global'),
        { id: 'global', error: errorMsg },
      ]);
    }

    setIsPrinting(false);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
              Clearance Label Generator
            </Typography>

            <TextField
              id="lotNumbers"
              label="Enter Lot Numbers (one per line)"
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              value={lotNumbersInput}
              onChange={(e) => setLotNumbersInput(e.target.value)}
              placeholder="e.g. 269009&#10;L123456"
              sx={{ mb: 2, mt: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleGeneratePreview}
              disabled={isLoading || isDownloadingZPL || isDownloadingPDF || isPrinting}
              startIcon={
                isLoading ? <CircularProgress size={24} color="inherit" /> : null
              }
            >
              {isLoading ? 'Generating...' : 'Generate Labels'}
            </Button>

            {generatedLabels.length > 0 && !isLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleOpenPrintDialog}
                  disabled={isPrinting || isDownloadingZPL || isDownloadingPDF}
                  startIcon={
                    isPrinting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <PrintIcon />
                    )
                  }
                >
                  {isPrinting ? 'Printing...' : 'Print to Zebra Printer'}
                </Button>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    onClick={handleDownloadZPL}
                    disabled={isDownloadingZPL || isDownloadingPDF || isPrinting}
                    startIcon={
                      isDownloadingZPL ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                  >
                    {isDownloadingZPL ? 'Generating...' : 'Download ZPL'}
                  </Button>

                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    size="large"
                    onClick={handleDownloadPDF}
                    disabled={!libsLoaded || isDownloadingZPL || isDownloadingPDF || isPrinting}
                    startIcon={
                      isDownloadingPDF ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <PictureAsPdfIcon />
                      )
                    }
                  >
                    {isDownloadingPDF ? 'Generating...' : 'Download PDF'}
                  </Button>
                </Box>
              </Box>
            )}

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 3 }}>
                <AlertTitle>Errors</AlertTitle>
                <List dense sx={{ listStyle: 'none', p: 0 }}>
                  {errors.map((err, index) => (
                    <ListItem key={index} sx={{ p: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ErrorOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={err.id !== 'global' ? `Lot ${err.id}` : 'Error'}
                        secondary={err.error || 'An unknown error occurred.'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
            Label Preview
            {generatedLabels.length > 0 && ` (${generatedLabels.length})`}
          </Typography>

          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}

          {!isLoading && generatedLabels.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                borderStyle: 'dashed',
                borderColor: 'grey.400',
                bgcolor: 'grey.50',
              }}
            >
              <Typography color="text.secondary">
                Previews will appear here once generated.
              </Typography>
            </Paper>
          )}

          {!isLoading && generatedLabels.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: { xs: 'center', lg: 'flex-start' },
              }}
            >
              {generatedLabels.map((labelData) => (
                <LabelPreview key={labelData.id} data={labelData} />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>

      <Box
        ref={printContainerRef}
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {generatedLabels.map((labelData) => (
          <LabelPreviewPDF key={`pdf-${labelData.id}`} data={labelData} />
        ))}
      </Box>

      {/* Printer Selection Dialog */}
      <Dialog
        open={showPrinterDialog}
        onClose={() => setShowPrinterDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Printer</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Printer</InputLabel>
            <Select
              value={selectedPrinter}
              label="Printer"
              onChange={(e) => setSelectedPrinter(e.target.value)}
            >
              {availablePrinters.length === 0 && (
                <MenuItem disabled>No printers found</MenuItem>
              )}
              {availablePrinters.map((printer) => (
                <MenuItem key={printer.name} value={printer.name}>
                  {printer.name} {printer.isDefault && '(Default)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {generatedLabels.length} label(s) will be sent to the selected printer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrinterDialog(false)}>Cancel</Button>
          <Button
            onClick={handlePrintToZebra}
            variant="contained"
            disabled={!selectedPrinter}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}